import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Switch,
  Box,
  Alert,
  CircularProgress,
  Typography,
  Tooltip,
  IconButton,
  Tabs,
  Tab,
  alpha,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useNotify } from 'react-admin';
import authInstance from '../../services/httpClient';

// =============================================================================
// Types
// =============================================================================

type HomeFilterType = 'category' | 'tag' | 'brand' | 'newOrPopular' | 'combined' | 'none';

interface CombinedFilterValue {
  categories: string[];
  tags: string[];
}

interface CategoryFormData {
  name: string;
  isActive: boolean;
  respectsEarlyCutoff: boolean;
  showOnHomePage: boolean;
  showInCarousel: boolean;
  showInCarouselWeb: boolean;
  showInShop: boolean;
  showTagFilters: boolean;
  homeFilterType: HomeFilterType;
  homeFilterValue: string;
  combinedCategories: string[];
  combinedTags: string[];
  image?: File | null;
}

interface Category {
  id: string;
  name: string;
  imageUrl: string | null;
  isActive: boolean;
  respectsEarlyCutoff: boolean;
  showOnHomePage: boolean;
  showInCarousel: boolean;
  showInCarouselWeb: boolean;
  showInShop: boolean;
  showTagFilters: boolean;
  homeFilterType: HomeFilterType;
  homeFilterValue: string | null;
}

interface InitialVisibility {
  showOnHomePage?: boolean;
  showInCarousel?: boolean;
  showInShop?: boolean;
}

/**
 * The subset of a category the draft editor hands back. Mirrors the editable
 * fields on CategoryDraft (pages/HomeLayoutManager/types.ts); positions and ids
 * are not the form's business.
 */
export interface CategoryDraftChanges {
  name: string;
  isActive: boolean;
  respectsEarlyCutoff: boolean;
  showOnHomePage: boolean;
  showInCarousel: boolean;
  showInCarouselWeb: boolean;
  showInShop: boolean;
  showTagFilters: boolean;
  homeFilterType: HomeFilterType;
  homeFilterValue: string | null;
  /** Only present when a new icon was uploaded in this session. */
  imageUrl?: string;
  /** The stored filename for that icon — what Publish Live writes to the category. */
  image?: string;
}

interface CategoryFormDialogProps {
  open: boolean;
  onClose: () => void;
  category?: Category | null;
  onSave: () => void;
  initialVisibility?: InitialVisibility;
  /**
   * Edit the DRAFT instead of the live category.
   *
   * Without this the dialog PATCHes /admin/categories/:id and the change is live
   * the moment you press Save — wrong inside Content Control, whose whole model
   * is edit-then-Publish-Live. In draft mode no category row is written: the
   * icon, if one was picked, goes to POST /admin/categories/icon (which stores
   * the file and touches no category), and the fields are handed to onDraftSave
   * for the caller to merge into the draft.
   */
  draftMode?: boolean;
  /** Required when draftMode is set. Receives the edited fields. */
  onDraftSave?: (changes: CategoryDraftChanges) => void;
}

// =============================================================================
// Styled Components / Design System
// =============================================================================

const COLORS = {
  background: '#1C1C1E',
  cardBackground: '#2C2C2E',
  cardBackgroundHover: '#3C3C3E',
  primary: '#FF3B30',
  primaryLight: '#FF6B6B',
  text: '#FFFFFF',
  textSecondary: '#8E8E93',
  textTertiary: '#636366',
  border: '#38383A',
  success: '#30D158',
  warning: '#FF9500',
  info: '#0A84FF',
};

// Reusable card wrapper component
const SettingsCard: React.FC<{
  children: React.ReactNode;
  title?: string;
  icon?: React.ReactNode;
  tooltip?: string;
}> = ({ children, title, icon, tooltip }) => (
  <Box
    sx={{
      bgcolor: COLORS.cardBackground,
      borderRadius: 3,
      p: 2.5,
      mb: 2,
    }}
  >
    {title && (
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {icon && (
          <Box sx={{ mr: 1.5, color: COLORS.textSecondary, display: 'flex' }}>
            {icon}
          </Box>
        )}
        <Typography variant="subtitle2" sx={{ color: COLORS.text, fontWeight: 600, letterSpacing: 0.5 }}>
          {title}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip}>
            <InfoOutlinedIcon sx={{ ml: 1, fontSize: 16, color: COLORS.textTertiary }} />
          </Tooltip>
        )}
      </Box>
    )}
    {children}
  </Box>
);

// Toggle row component for consistent styling
const ToggleRow: React.FC<{
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  tooltip?: string;
  color?: 'primary' | 'error' | 'info' | 'success' | 'warning';
}> = ({ label, checked, onChange, tooltip, color = 'primary' }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      py: 1.5,
      '&:not(:last-child)': {
        borderBottom: `1px solid ${COLORS.border}`,
      },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Typography variant="body2" sx={{ color: COLORS.text }}>
        {label}
      </Typography>
      {tooltip && (
        <Tooltip title={tooltip}>
          <InfoOutlinedIcon sx={{ ml: 1, fontSize: 14, color: COLORS.textTertiary }} />
        </Tooltip>
      )}
    </Box>
    <Switch
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      color={color}
      sx={{
        '& .MuiSwitch-switchBase.Mui-checked': {
          color: color === 'error' ? COLORS.primary : undefined,
        },
        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
          backgroundColor: color === 'error' ? COLORS.primary : undefined,
        },
      }}
    />
  </Box>
);

// Grid toggle card for 2x2 layout
const GridToggleCard: React.FC<{
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  tooltip?: string;
  icon?: React.ReactNode;
}> = ({ label, checked, onChange, tooltip, icon }) => (
  <Box
    onClick={() => onChange(!checked)}
    sx={{
      bgcolor: checked ? alpha(COLORS.primary, 0.15) : COLORS.cardBackground,
      border: `2px solid ${checked ? COLORS.primary : COLORS.border}`,
      borderRadius: 2,
      p: 2,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:hover': {
        bgcolor: checked ? alpha(COLORS.primary, 0.2) : COLORS.cardBackgroundHover,
      },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
      {icon && (
        <Box sx={{ color: checked ? COLORS.primary : COLORS.textSecondary }}>
          {icon}
        </Box>
      )}
      <Switch
        checked={checked}
        onChange={(e) => {
          e.stopPropagation();
          onChange(e.target.checked);
        }}
        size="small"
        color="error"
        sx={{
          '& .MuiSwitch-switchBase.Mui-checked': {
            color: COLORS.primary,
          },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: COLORS.primary,
          },
        }}
      />
    </Box>
    <Typography
      variant="caption"
      sx={{
        color: checked ? COLORS.text : COLORS.textSecondary,
        fontWeight: checked ? 600 : 400,
        display: 'block',
      }}
    >
      {label}
    </Typography>
    {tooltip && (
      <Typography variant="caption" sx={{ color: COLORS.textTertiary, fontSize: 10, mt: 0.5, display: 'block' }}>
        {tooltip}
      </Typography>
    )}
  </Box>
);

// =============================================================================
// Main Component
// =============================================================================

const CategoryFormDialog: React.FC<CategoryFormDialogProps> = ({
  open,
  onClose,
  category,
  onSave,
  initialVisibility,
  draftMode = false,
  onDraftSave,
}) => {
  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  const [formData, setFormData] = useState<CategoryFormData>({
    name: '',
    isActive: true,
    respectsEarlyCutoff: false,
    showOnHomePage: false,
    showInCarousel: false,
    showInCarouselWeb: false,
    showInShop: false,
    showTagFilters: false,
    homeFilterType: 'category',
    homeFilterValue: '',
    combinedCategories: [],
    combinedTags: [],
    image: null,
  });
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const notify = useNotify();

  const isEditMode = !!category;

  // Reset tab when dialog opens/closes
  useEffect(() => {
    if (open) {
      setActiveTab(0);
    }
  }, [open]);

  // Initialize form data
  useEffect(() => {
    if (category) {
      const filterType = (category.homeFilterType || 'category') as HomeFilterType;
      let filterValue = category.homeFilterValue || '';
      let combinedCategories: string[] = [];
      let combinedTags: string[] = [];

      if (filterType === 'category' && !filterValue) {
        filterValue = category.name;
      }

      if (filterType === 'combined' && filterValue) {
        try {
          const parsed = JSON.parse(filterValue) as CombinedFilterValue;
          combinedCategories = parsed.categories || [];
          combinedTags = parsed.tags || [];
          filterValue = '';
        } catch (e) {
          console.warn('Failed to parse combined filter value:', e);
        }
      }

      setFormData({
        name: category.name,
        isActive: category.isActive,
        respectsEarlyCutoff: category.respectsEarlyCutoff,
        showOnHomePage: category.showOnHomePage,
        showInCarousel: category.showInCarousel ?? false,
        showInCarouselWeb: category.showInCarouselWeb ?? false,
        showInShop: category.showInShop ?? true,
        showTagFilters: category.showTagFilters || false,
        homeFilterType: filterType,
        homeFilterValue: filterValue,
        combinedCategories,
        combinedTags,
        image: null,
      });
      setImagePreview(category.imageUrl);
    } else {
      setFormData({
        name: '',
        isActive: true,
        respectsEarlyCutoff: false,
        showOnHomePage: initialVisibility?.showOnHomePage ?? false,
        showInCarousel: initialVisibility?.showInCarousel ?? false,
        showInCarouselWeb: false,
        showInShop: initialVisibility?.showInShop ?? false,
        showTagFilters: false,
        homeFilterType: 'category',
        homeFilterValue: '',
        combinedCategories: [],
        combinedTags: [],
        image: null,
      });
      setImagePreview(null);
    }
    setError(null);
  }, [category, open, initialVisibility]);

  // Event handlers
  const handleInputChange = (field: keyof CategoryFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleToggleChange = (field: keyof CategoryFormData, value: boolean) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = ['image/svg+xml', 'image/png', 'image/jpeg', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setError('Please select a valid image file (SVG, PNG, JPEG, or WebP)');
        return;
      }

      if (file.size > 2 * 1024 * 1024) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
        setError(`Image file is too large (${sizeMB}MB). Please choose an image smaller than 2MB.`);
        return;
      }

      setFormData({ ...formData, image: file });

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError('Category name is required');
      return false;
    }

    if (formData.name.trim().length < 3) {
      setError('Category name must be at least 3 characters');
      return false;
    }

    if (formData.name.trim().length > 50) {
      setError('Category name must be at most 50 characters');
      return false;
    }

    if (!isEditMode && !formData.image) {
      setError('Category icon is required for new categories');
      return false;
    }

    return true;
  };

  /**
   * homeFilterValue is one string carrying three different shapes: JSON for
   * 'combined', a plain value for category/tag/brand, empty otherwise. Both the
   * live and the draft save paths write it, so it is built in one place —
   * letting the two drift is how a draft would publish a filter the live editor
   * would have written differently.
   */
  const serializeHomeFilterValue = (): string => {
    if (formData.homeFilterType === 'combined') {
      const combinedValue: CombinedFilterValue = {
        categories: formData.combinedCategories,
        tags: formData.combinedTags,
      };
      return JSON.stringify(combinedValue);
    }
    if (
      (formData.homeFilterType === 'category'
        || formData.homeFilterType === 'tag'
        || formData.homeFilterType === 'brand')
      && formData.homeFilterValue
    ) {
      return formData.homeFilterValue;
    }
    return '';
  };

  /**
   * Draft save — hand the fields back to the caller, write no category row.
   *
   * The icon is the one field that cannot simply be held in memory: it is a File
   * that has to reach storage before anything can render it. POST
   * /admin/categories/icon stores it and returns a URL without touching a
   * category, so the draft carries the URL and Publish Live writes it through
   * with every other field. If the upload fails we stop rather than saving the
   * rest — a half-applied edit is harder to reason about than a failed one.
   */
  const submitToDraft = async () => {
    let imageUrl: string | undefined;
    let image: string | undefined;

    if (formData.image) {
      const iconForm = new FormData();
      iconForm.append('image', formData.image);
      const { data } = await authInstance.post('/admin/categories/icon', iconForm);
      imageUrl = data?.imageUrl;
      image = data?.image;
      if (!imageUrl || !image) throw new Error('Icon upload returned no URL');
    }

    onDraftSave?.({
      name: formData.name,
      isActive: formData.isActive,
      respectsEarlyCutoff: formData.respectsEarlyCutoff,
      showOnHomePage: formData.showOnHomePage,
      showInCarousel: formData.showInCarousel,
      showInCarouselWeb: formData.showInCarouselWeb,
      showInShop: formData.showInShop,
      showTagFilters: formData.showTagFilters,
      homeFilterType: formData.homeFilterType,
      homeFilterValue: serializeHomeFilterValue() || null,
      ...(imageUrl && image ? { imageUrl, image } : {}),
    });

    notify(`Category "${formData.name}" updated in draft — Publish Live to apply`, {
      type: 'success',
    });
    onClose();
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      if (draftMode) {
        await submitToDraft();
        return;
      }

      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('isActive', String(formData.isActive));
      formDataToSend.append('respectsEarlyCutoff', String(formData.respectsEarlyCutoff));
      formDataToSend.append('showOnHomePage', String(formData.showOnHomePage));
      formDataToSend.append('showInCarousel', String(formData.showInCarousel));
      formDataToSend.append('showInCarouselWeb', String(formData.showInCarouselWeb));
      formDataToSend.append('showInShop', String(formData.showInShop));
      formDataToSend.append('showTagFilters', String(formData.showTagFilters));
      formDataToSend.append('homeFilterType', formData.homeFilterType);

      formDataToSend.append('homeFilterValue', serializeHomeFilterValue());

      if (formData.image) {
        formDataToSend.append('image', formData.image);
      }

      const apiUrl = isEditMode
        ? `/admin/categories/${category.id}`
        : '/admin/categories';

      if (isEditMode) {
        await authInstance.patch(apiUrl, formDataToSend);
      } else {
        await authInstance.post(apiUrl, formDataToSend);
      }

      notify(
        isEditMode ? `Category "${formData.name}" updated` : `Category "${formData.name}" created`,
        { type: 'success' }
      );
      onSave();
      onClose();
    } catch (err: any) {
      const errorMessage = err?.message || 'Failed to save category';
      setError(errorMessage);
      notify(errorMessage, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // =============================================================================
  // Tab Content Renderers
  // =============================================================================

  const renderGeneralTab = () => (
    <Box sx={{ pt: 2 }}>
      {/* Hero Image Uploader */}
      <SettingsCard>
        <Box
          sx={{
            width: '100%',
            aspectRatio: '16/9',
            maxHeight: 200,
            bgcolor: COLORS.background,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            position: 'relative',
            border: `2px dashed ${imagePreview ? 'transparent' : COLORS.border}`,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: !imagePreview ? COLORS.primary : 'transparent',
              '& .upload-overlay': {
                opacity: 1,
              },
            },
          }}
          component="label"
        >
          {imagePreview ? (
            <>
              <img
                src={imagePreview}
                alt="Category preview"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
              <Box
                className="upload-overlay"
                sx={{
                  position: 'absolute',
                  inset: 0,
                  bgcolor: 'rgba(0,0,0,0.6)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0,
                  transition: 'opacity 0.2s ease',
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 40, color: COLORS.text, mb: 1 }} />
                <Typography variant="body2" sx={{ color: COLORS.text }}>
                  Change Image
                </Typography>
              </Box>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <CloudUploadIcon sx={{ fontSize: 48, color: COLORS.textTertiary, mb: 1 }} />
              <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                Click to upload category icon
              </Typography>
              <Typography variant="caption" sx={{ color: COLORS.textTertiary, mt: 0.5, display: 'block' }}>
                156×156px PNG or SVG recommended • Max 2MB
              </Typography>
            </Box>
          )}
          <input type="file" accept="image/*" hidden onChange={handleImageChange} />
        </Box>
      </SettingsCard>

      {/* Category Name */}
      <SettingsCard>
        <TextField
          fullWidth
          label="Category Name"
          value={formData.name}
          onChange={handleInputChange('name')}
          required
          variant="outlined"
          placeholder="Enter category name"
          helperText="3-50 characters, must be unique"
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: COLORS.background,
              '& fieldset': {
                borderColor: COLORS.border,
              },
              '&:hover fieldset': {
                borderColor: COLORS.textSecondary,
              },
              '&.Mui-focused fieldset': {
                borderColor: COLORS.primary,
              },
            },
            '& .MuiInputLabel-root': {
              color: COLORS.textSecondary,
            },
            '& .MuiOutlinedInput-input': {
              color: COLORS.text,
            },
            '& .MuiFormHelperText-root': {
              color: COLORS.textTertiary,
            },
          }}
        />
      </SettingsCard>

      {/* Active Toggle */}
      <SettingsCard title="Status">
        <ToggleRow
          label="Active"
          checked={formData.isActive}
          onChange={(checked) => handleToggleChange('isActive', checked)}
          tooltip="When enabled, this category is visible in the mobile app"
          color="success"
        />
      </SettingsCard>
    </Box>
  );

  const renderVisibilityTab = () => (
    <Box sx={{ pt: 2 }}>
      {/* Display Visibility - 2x2 Grid */}
      <SettingsCard
        title="Display Locations"
        icon={<VisibilityOutlinedIcon fontSize="small" />}
        tooltip="Control where this category appears"
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 1.5,
          }}
        >
          <GridToggleCard
            label="Mobile Carousel"
            checked={formData.showInCarousel}
            onChange={(checked) => handleToggleChange('showInCarousel', checked)}
            tooltip="Home page icon row"
          />
          <GridToggleCard
            label="Web Carousel"
            checked={formData.showInCarouselWeb}
            onChange={(checked) => handleToggleChange('showInCarouselWeb', checked)}
            tooltip="Web home page icons"
          />
          <GridToggleCard
            label="Home Page"
            checked={formData.showOnHomePage}
            onChange={(checked) => handleToggleChange('showOnHomePage', checked)}
            tooltip="Product scroller section"
          />
          <GridToggleCard
            label="Shop Page"
            checked={formData.showInShop}
            onChange={(checked) => handleToggleChange('showInShop', checked)}
            tooltip="Category grid"
          />
        </Box>
      </SettingsCard>
    </Box>
  );

  // =============================================================================
  // Render
  // =============================================================================

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: COLORS.background,
            backgroundImage: 'none',
            borderRadius: 3,
            maxHeight: '90vh',
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${COLORS.border}`,
            py: 2,
            px: 3,
          }}
        >
          <Typography variant="h6" component="span" sx={{ color: COLORS.text, fontWeight: 600 }}>
            {isEditMode ? 'Edit Category' : 'New Category'}
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: COLORS.textSecondary }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Segmented Control (Tabs) */}
        <Box sx={{ px: 3, pt: 2, bgcolor: COLORS.background }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="fullWidth"
            sx={{
              minHeight: 40,
              bgcolor: COLORS.cardBackground,
              borderRadius: 2,
              p: 0.5,
              '& .MuiTabs-indicator': {
                display: 'none',
              },
              '& .MuiTab-root': {
                minHeight: 36,
                borderRadius: 1.5,
                color: COLORS.textSecondary,
                fontSize: '0.875rem',
                fontWeight: 500,
                textTransform: 'none',
                transition: 'all 0.2s ease',
                '&.Mui-selected': {
                  bgcolor: COLORS.background,
                  color: COLORS.text,
                },
              },
            }}
          >
            <Tab label="General" />
            <Tab label="Visibility" />
          </Tabs>
        </Box>

        {/* Error Alert */}
        {error && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Alert
              severity="error"
              onClose={() => setError(null)}
              sx={{
                bgcolor: alpha('#FF3B30', 0.1),
                color: '#FF3B30',
                '& .MuiAlert-icon': { color: '#FF3B30' },
              }}
            >
              {error}
            </Alert>
          </Box>
        )}

        {/* Content */}
        <DialogContent sx={{ px: 3, py: 0, bgcolor: COLORS.background }}>
          {activeTab === 0 && renderGeneralTab()}
          {activeTab === 1 && renderVisibilityTab()}
        </DialogContent>

        {/* Actions */}
        <DialogActions
          sx={{
            borderTop: `1px solid ${COLORS.border}`,
            px: 3,
            py: 2,
            bgcolor: COLORS.background,
          }}
        >
          <Button
            onClick={onClose}
            disabled={loading}
            sx={{
              color: COLORS.textSecondary,
              '&:hover': {
                bgcolor: alpha(COLORS.text, 0.05),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            sx={{
              bgcolor: COLORS.primary,
              color: COLORS.text,
              fontWeight: 600,
              px: 4,
              '&:hover': {
                bgcolor: COLORS.primaryLight,
              },
              '&:disabled': {
                bgcolor: alpha(COLORS.primary, 0.5),
              },
            }}
          >
            {loading ? <CircularProgress size={20} sx={{ color: COLORS.text }} /> : isEditMode ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Product Preview Modal */}
    </>
  );
};

export default CategoryFormDialog;
